import * as React from 'react';
import { Typography, Box, Container, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { centered_flex_box, main_button, sec_button } from '../../app/components/Styles';
import { AddCircleOutline, Visibility, Print } from '@mui/icons-material';
import { useEffect } from 'react';
import { Table } from 'antd';
import { antTableWrapperSx, tableShowTotal, idTableColumn } from '../../app/components/AntTableStyles';
import moment from 'moment';

const TASK_STATUS_COLORS = {
    processing: 'warning',
    completed: 'success',
    failed: 'error',
};

const typeLabel = (kind) => {
    if (kind === 'outbound') return 'Outbound';
    if (kind === 'recount') return 'Stock recount';
    return 'Inbound';
};

export const ItemUpdateHistory = ({
    getItemUpdateTasks,
    itemUpdateTasks,
    getStockRecounts,
    stockRecounts,
    isLoading,
    progressBase,
    recountProgressBase,
    homePath,
    downloadStockRecountReceipt,
}) => {
    const navigate = useNavigate();
    const [page, setPage] = React.useState(1);
    const [kind, setKind] = React.useState('');
    const limit = 20;
    const includeRecounts = Boolean(getStockRecounts);

    useEffect(() => {
        const recountOnly = kind === 'recount';
        const movementOnly = kind === 'inbound' || kind === 'outbound';
        if (!recountOnly) {
            getItemUpdateTasks({
                page,
                limit,
                ...(kind && kind !== 'recount' ? { kind } : {}),
            });
        }
        if (includeRecounts && !movementOnly) {
            getStockRecounts({ page, limit });
        }
    }, [page, kind]);

    const movementRows = (kind === 'recount' ? [] : (itemUpdateTasks?.docs || [])).map((row) => ({
        ...row,
        kind: row.kind,
    }));
    const recountRows = (!includeRecounts || kind === 'inbound' || kind === 'outbound')
        ? []
        : (stockRecounts?.docs || []).map((row) => ({
            _id: row._id,
            kind: 'recount',
            status: row.status,
            summary: row.summary,
            createdAt: row.createdAt,
            brandName: row.vendor?.brandName || '—',
        }));
    const rows = [...movementRows, ...recountRows].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
    const total = kind === 'recount'
        ? (stockRecounts?.totalDocs || 0)
        : (kind === 'inbound' || kind === 'outbound')
            ? (itemUpdateTasks?.totalDocs || 0)
            : (itemUpdateTasks?.totalDocs || 0) + (includeRecounts ? (stockRecounts?.totalDocs || 0) : 0);

    const columns = [
        idTableColumn(),
        { title: 'Type', dataIndex: 'kind', render: (value) => typeLabel(value) },
        { title: 'Brand', dataIndex: 'brandName', render: (value) => value || '—' },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Chip label={status || ''} color={TASK_STATUS_COLORS[status] || 'default'} size="small" />
            ),
        },
        { title: 'Lines', render: (_, row) => row.summary?.total ?? 0 },
        { title: 'Success', render: (_, row) => row.summary?.success ?? 0 },
        { title: 'Failed', render: (_, row) => row.summary?.failed ?? 0 },
        { title: 'Created', dataIndex: 'createdAt', render: (value) => value ? moment(value).format('lll') : '' },
        {
            title: '',
            render: (_, row) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {row.kind === 'recount' && downloadStockRecountReceipt && (
                        <Button
                            size="small"
                            startIcon={<Print />}
                            onClick={() => downloadStockRecountReceipt(row._id)}
                        >
                            Receipt
                        </Button>
                    )}
                    <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => navigate(row.kind === 'recount'
                            ? `${recountProgressBase}/${row._id}`
                            : `${progressBase}/${row._id}`)}
                    >
                        Open
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ ...centered_flex_box, mb: 2, gap: 1, justifyContent: 'space-between' }}>
                <Typography variant="h4" sx={{ color: 'var(--secColor)' }}>Item updates</Typography>
                {includeRecounts && (
                    <Button sx={main_button} startIcon={<AddCircleOutline />} onClick={() => navigate('/ims/admin/stock-recount')}>
                        New recount
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button sx={kind === '' ? main_button : sec_button} onClick={() => { setKind(''); setPage(1); }}>All</Button>
                <Button sx={kind === 'inbound' ? main_button : sec_button} onClick={() => { setKind('inbound'); setPage(1); }}>Inbound</Button>
                <Button sx={kind === 'outbound' ? main_button : sec_button} onClick={() => { setKind('outbound'); setPage(1); }}>Outbound</Button>
                {includeRecounts && (
                    <Button sx={kind === 'recount' ? main_button : sec_button} onClick={() => { setKind('recount'); setPage(1); }}>Stock recount</Button>
                )}
                <Button sx={sec_button} onClick={() => navigate(homePath)}>Home</Button>
            </Box>
            <Box sx={antTableWrapperSx(false)}>
                <Table
                    rowKey={(row) => `${row.kind}-${row._id}`}
                    columns={columns}
                    dataSource={rows}
                    loading={isLoading}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total,
                        showTotal: tableShowTotal('records'),
                        onChange: (next) => setPage(next),
                    }}
                />
            </Box>
        </Container>
    );
};
